/**
 * Anchor Navigation with Blogs Section JavaScript
 * Handles smooth scrolling and active state management
 */

class AnchorNavigation {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.anchorLinks = document.querySelectorAll(`#${sectionId} .anchor-nav-link`);
    this.contentBlocks = document.querySelectorAll(`#${sectionId} .content-block`);
    this.isScrolling = false;

    // Drag scroll properties
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.dragDistance = 0;
    this.isClick = true;

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupIntersectionObserver();

    // Only for desktop, mobile keep default
    if (window.innerWidth > 750) {
      this.setupDragScroll();
    }
  }

  setupEventListeners() {
    // Handle anchor link clicks
    this.anchorLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        this.handleAnchorClick(event);
      });
    });

    // Handle scroll events
    window.addEventListener('scroll', () => {
      this.handleScroll();
    });
  }

  handleAnchorClick(event) {
    event.preventDefault();
    const targetId = event.currentTarget.getAttribute('href').substring(1);
    this.scrollToBlock(targetId);
    this.updateActiveState(targetId);
  }

  scrollToBlock(blockId) {
    const targetBlock = document.getElementById(blockId);
    if (targetBlock) {
      targetBlock.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  updateActiveState(blockId) {
    // Remove active class from all links
    this.anchorLinks.forEach((link) => link.classList.remove('active'));

    // Add active class to current link
    const currentLink = document.querySelector(`#${this.sectionId} .anchor-nav-link[href="#${blockId}"]`);
    if (currentLink) {
      currentLink.classList.add('active');
    }
  }

  setupIntersectionObserver() {
    const observerOptions = {
      rootMargin: '-100px 0px -50% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateActiveState(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all content blocks
    this.contentBlocks.forEach((block) => {
      observer.observe(block);
    });
  }

  handleScroll() {
    if (!this.isScrolling) {
      this.isScrolling = true;

      setTimeout(() => {
        this.isScrolling = false;
      }, 100);
    }
  }

  setupDragScroll() {
    const scrollContainers = document.querySelectorAll(`#${this.sectionId} .blog-posts-scroll`);

    scrollContainers.forEach((container) => {
      // Mouse events
      container.addEventListener('mousedown', (e) => this.startDragging(e, container));
      container.addEventListener('mousemove', (e) => this.drag(e, container));
      container.addEventListener('mouseup', () => this.stopDragging(container));
      container.addEventListener('mouseleave', () => this.stopDragging(container));

      // Touch events for mobile
      container.addEventListener('touchstart', (e) => this.startDragging(e, container));
      container.addEventListener('touchmove', (e) => this.drag(e, container));
      container.addEventListener('touchend', () => this.stopDragging(container));

      // Prevent default drag behavior on images
      const images = container.querySelectorAll('img');
      images.forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Handle click events on blog post links
      const blogLinks = container.querySelectorAll('.blog-post-link');
      blogLinks.forEach((link) => {
        link.addEventListener('click', (e) => this.handleBlogLinkClick(e));
      });
    });
  }

  startDragging(e, container) {
    this.isDragging = true;
    this.isClick = true;
    this.dragDistance = 0;
    container.classList.add('dragging');

    // Get starting position
    if (e.type === 'mousedown') {
      this.startX = e.pageX - container.offsetLeft;
    } else if (e.type === 'touchstart') {
      this.startX = e.touches[0].pageX - container.offsetLeft;
    }

    this.scrollLeft = container.scrollLeft;
  }

  drag(e, container) {
    if (!this.isDragging) return;

    e.preventDefault();

    // Calculate new position
    let currentX;
    if (e.type === 'mousemove') {
      currentX = e.pageX - container.offsetLeft;
    } else if (e.type === 'touchmove') {
      currentX = e.touches[0].pageX - container.offsetLeft;
    }

    // Calculate distance moved
    const distance = currentX - this.startX;
    this.dragDistance = Math.abs(distance);

    // If dragged more than 5px, it's not a click
    if (this.dragDistance > 5) {
      this.isClick = false;
    }

    // Scroll the container
    container.scrollLeft = this.scrollLeft - distance;
  }

  stopDragging(container) {
    this.isDragging = false;
    container.classList.remove('dragging');

    // Reset click state after a short delay
    setTimeout(() => {
      this.isClick = true;
      this.dragDistance = 0;
    }, 100);
  }

  handleBlogLinkClick(e) {
    // If it was a drag operation, prevent navigation
    if (!this.isClick || this.dragDistance > 5) {
      e.preventDefault();
      return false;
    }

    // Allow normal navigation for actual clicks
    return true;
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
  // Find all anchor navigation sections
  const anchorSections = document.querySelectorAll('.anchor-navigation-with-blogs');

  anchorSections.forEach((section) => {
    const sectionId = section.id;
    if (sectionId) {
      new AnchorNavigation(sectionId);
    }
  });
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AnchorNavigation;
}
